import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Lock, RotateCcw, HandHeart } from "lucide-react";

export const COMMISSION = {
  nameInput: "commission-name-input",
  emailInput: "commission-email-input",
  phoneInput: "commission-phone-input",
  petNameInput: "commission-pet-name-input",
  petSpeciesInput: "commission-pet-species-input",
  messageInput: "commission-message-input",
  submitBtn: "commission-submit-btn",
};

export default function Commission() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const memorialSlug = params.get("memorial");
  const [memorial, setMemorial] = useState(null);
  const [config, setConfig] = useState({ diorama_starting_price: "150", contact_email: "" });
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    pet_name: "",
    pet_species: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [createdInquiry, setCreatedInquiry] = useState(null);
  const [depositBusy, setDepositBusy] = useState(false);
  const [paypalBusy, setPaypalBusy] = useState(false);

  useEffect(() => {
    apiClient.get("/config").then(({ data }) => setConfig(data)).catch(() => {});
    if (memorialSlug) {
      apiClient.get(`/memorials/${memorialSlug}`).then(({ data }) => {
        setMemorial(data);
        setForm((p) => ({ ...p, pet_name: data.pet_name || "", pet_species: data.species || "" }));
      }).catch(() => {});
    }
  }, [memorialSlug]);

  const upd = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim() || !form.customer_email.trim()) {
      toast.error("Please share your name and email.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await apiClient.post("/inquiries", {
        ...form,
        memorial_id: memorial?.id || null,
        memorial_slug: memorial?.share_slug || null,
        scenery_image_base64: memorial?.scenery_image_base64 || null,
        scenery_mime_type: memorial?.scenery_mime_type || null,
        poem: memorial?.poem || null,
      });
      setCreatedInquiry(data);
      setSent(true);
      toast.success("Inquiry received — we'll be in touch soon ✦");
    } catch (e) {
      console.error(e);
      toast.error("Could not send. Please email us directly.");
    } finally {
      setBusy(false);
    }
  };

  const reserveWithDeposit = async () => {
    if (!createdInquiry) return;
    setDepositBusy(true);
    try {
      const { data } = await apiClient.post("/payments/checkout/deposit", {
        inquiry_id: createdInquiry.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setDepositBusy(false);
    }
  };

  const reserveWithPayPal = async () => {
    if (!createdInquiry) return;
    setPaypalBusy(true);
    try {
      const { data } = await apiClient.post("/paypal/checkout/deposit", {
        inquiry_id: createdInquiry.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.approval_url;
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not start PayPal checkout.");
      setPaypalBusy(false);
    }
  };

  if (sent) {
    return (
      <main className="relative max-w-2xl mx-auto px-6 py-24 text-center">
        <div className="hero-card rounded-sm px-10 py-16">
          <div className="tracking-celestial text-[var(--rb-gold)] mb-4">Thank you, kindly.</div>
          <h1 className="font-serif-display text-4xl text-[var(--rb-text-heading)] mb-6">Your inquiry is on its way.</h1>
          <p className="font-serif-display italic text-[18px] text-[var(--rb-text-body)] leading-relaxed">
            We will reach out personally within a day or two to begin shaping a diorama that
            honors {form.pet_name || "your dear friend"} just as they deserve.
          </p>
          <div className="ornament mt-8 mb-8"><span>✦</span></div>
          <div className="bg-[var(--rb-gold-soft)]/30 border border-[var(--rb-gold)]/30 rounded-sm p-6 mb-8 text-left">
            <div className="tracking-celestial text-[var(--rb-gold)] mb-2">Optional · Reserve your place</div>
            <p className="font-serif-display italic text-[16px] text-[var(--rb-text-body)] leading-relaxed">
              If you&apos;d like to secure your spot in the queue, a refundable
              <span className="font-serif-display text-[var(--rb-gold)] text-[20px]"> ${config.diorama_deposit_usd || 50} </span>
              deposit holds your commission and is applied toward the final price. Pay anytime
              — by email reply or right now.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button onClick={reserveWithDeposit} disabled={depositBusy || paypalBusy} className="btn-gold disabled:opacity-50" data-testid="commission-pay-deposit-btn">
              {depositBusy ? "Opening secure checkout…" : `Reserve with card · $${config.diorama_deposit_usd || 50}`}
            </button>
            <button onClick={reserveWithPayPal} disabled={depositBusy || paypalBusy} data-testid="commission-pay-paypal-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FFC439] hover:bg-[#F5B93B] text-[#003087] font-bold text-[13px] tracking-wide disabled:opacity-50 transition-colors">
              <svg viewBox="0 0 100 30" width="70" height="20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <text x="0" y="22" fontFamily="Helvetica,Arial,sans-serif" fontWeight="700" fontSize="22" fill="#003087">Pay</text>
                <text x="42" y="22" fontFamily="Helvetica,Arial,sans-serif" fontWeight="700" fontSize="22" fill="#009cde">Pal</text>
              </svg>
              <span>{paypalBusy ? "Opening…" : `$${config.diorama_deposit_usd || 50}`}</span>
            </button>
            <button onClick={() => nav("/")} className="btn-ghost">Return home</button>
          </div>

          <div className="mt-8 pt-6 border-t border-[var(--rb-gold)]/20" data-testid="commission-trust-row">
            <div className="tracking-celestial text-[10px] text-[var(--rb-text-muted)] text-center mb-4">A promise, sealed in trust</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-left">
              <div className="flex items-start gap-3" data-testid="trust-stripe">
                <ShieldCheck size={18} className="text-[var(--rb-gold)] mt-0.5 shrink-0" strokeWidth={1.5}/>
                <div>
                  <div className="font-serif-display text-[13px] text-[var(--rb-text-heading)] leading-tight">Stripe-secured</div>
                  <div className="text-[11px] text-[var(--rb-text-muted)] italic leading-snug mt-0.5">Card data never touches our server</div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-testid="trust-refund">
                <RotateCcw size={18} className="text-[var(--rb-gold)] mt-0.5 shrink-0" strokeWidth={1.5}/>
                <div>
                  <div className="font-serif-display text-[13px] text-[var(--rb-text-heading)] leading-tight">Refund within 2 hours</div>
                  <div className="text-[11px] text-[var(--rb-text-muted)] italic leading-snug mt-0.5">Cancel before work begins · <a href="/return-policy" className="underline decoration-[var(--rb-gold)]/40 hover:text-[var(--rb-gold)]" data-testid="trust-policy-link">policy</a></div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-testid="trust-encrypted">
                <Lock size={18} className="text-[var(--rb-gold)] mt-0.5 shrink-0" strokeWidth={1.5}/>
                <div>
                  <div className="font-serif-display text-[13px] text-[var(--rb-text-heading)] leading-tight">256-bit encrypted</div>
                  <div className="text-[11px] text-[var(--rb-text-muted)] italic leading-snug mt-0.5">Every detail kept private &amp; safe</div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-testid="trust-handmade">
                <HandHeart size={18} className="text-[var(--rb-gold)] mt-0.5 shrink-0" strokeWidth={1.5}/>
                <div>
                  <div className="font-serif-display text-[13px] text-[var(--rb-text-heading)] leading-tight">Crafted by hand</div>
                  <div className="text-[11px] text-[var(--rb-text-muted)] italic leading-snug mt-0.5">By the studio that loves them too</div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center gap-2 mt-5 text-[10px] tracking-[0.25em] uppercase text-[var(--rb-text-muted)]">
              <span>Visa</span><span className="opacity-40">·</span>
              <span>Mastercard</span><span className="opacity-40">·</span>
              <span>Amex</span><span className="opacity-40">·</span>
              <span>Apple Pay</span><span className="opacity-40">·</span>
              <span>Google Pay</span>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="relative max-w-5xl mx-auto px-6 md:px-12 py-16">
      <div className="text-center mb-12">
        <div className="tracking-celestial text-[var(--rb-gold)] mb-3">A handcrafted heirloom</div>
        <h1 className="font-serif-display text-5xl md:text-6xl text-[var(--rb-text-heading)]">
          Commission a one-of-a-kind<br/><span className="italic">diorama for {memorial ? memorial.pet_name : "your friend"}.</span>
        </h1>
        <p className="mt-6 font-serif-display italic text-[18px] text-[var(--rb-text-body)] max-w-2xl mx-auto leading-relaxed">
          Each diorama is sculpted by hand from the scene you&apos;ve envisioned together —
          sealed beneath glimmering glass, finished with the verse written for them, and posted
          to your door as an heirloom that outlives any photograph.
        </p>
        <div className="mt-6 inline-flex items-center gap-3 px-5 py-2 rounded-full border border-[var(--rb-gold)]/40 bg-white/60">
          <span className="text-[11px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)]">Starting from</span>
          <span className="font-serif-display text-2xl text-[var(--rb-gold)]">${config.diorama_starting_price}</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <form onSubmit={submit} className="glass p-8 md:p-10 rounded-sm space-y-5">
            <div className="tracking-celestial text-[var(--rb-text-muted)] mb-2">Tell us about you</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="rb-label">Your name *</label>
                <input className="rb-input" value={form.customer_name} onChange={upd("customer_name")} required data-testid={COMMISSION.nameInput}/>
              </div>
              <div>
                <label className="rb-label">Your email *</label>
                <input type="email" className="rb-input" value={form.customer_email} onChange={upd("customer_email")} required data-testid={COMMISSION.emailInput}/>
              </div>
            </div>
            <div>
              <label className="rb-label">Phone (optional)</label>
              <input className="rb-input" value={form.customer_phone} onChange={upd("customer_phone")} data-testid={COMMISSION.phoneInput}/>
            </div>
            <div className="ornament my-4"><span>✦</span></div>
            <div className="tracking-celestial text-[var(--rb-text-muted)] mb-2">About your companion</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="rb-label">Their name</label>
                <input className="rb-input" value={form.pet_name} onChange={upd("pet_name")} data-testid={COMMISSION.petNameInput}/>
              </div>
              <div>
                <label className="rb-label">Species / breed</label>
                <input className="rb-input" value={form.pet_species} onChange={upd("pet_species")} data-testid={COMMISSION.petSpeciesInput}/>
              </div>
            </div>
            <div>
              <label className="rb-label">A note (optional)</label>
              <textarea className="rb-textarea" rows={5} value={form.message} onChange={upd("message")} placeholder="Anything that would help us bring their world to life..." data-testid={COMMISSION.messageInput}/>
            </div>
            <button type="submit" disabled={busy} className="btn-gold w-full justify-center" data-testid={COMMISSION.submitBtn}>
              {busy ? "Sending with care..." : "Request your diorama"}
            </button>
            <p className="text-[11px] text-center text-[var(--rb-text-muted)] italic mt-2">We reply personally within 1–2 days.</p>
          </form>
        </div>

        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-8 space-y-6">
            {memorial && (
              <div className="memorial-card overflow-hidden">
                {memorial.scenery_image_base64 && (
                  <img src={`data:${memorial.scenery_mime_type || "image/png"};base64,${memorial.scenery_image_base64}`} alt="" className="w-full aspect-[4/3] object-cover"/>
                )}
                <div className="p-8 text-center">
                  <div className="tracking-celestial text-[var(--rb-gold)] mb-2">Your envisioned scene</div>
                  <div className="font-serif-display italic text-3xl text-[var(--rb-text-heading)]">{memorial.pet_name}</div>
                  {memorial.dates && <div className="font-serif-display italic text-sm text-[var(--rb-text-muted)] mt-1">{memorial.dates}</div>}
                  {memorial.poem && <p className="font-serif-display italic text-[14px] text-[var(--rb-text-body)] mt-4 line-clamp-5 leading-relaxed">{memorial.poem.split("\n").slice(0,4).join("\n")}</p>}
                </div>
              </div>
            )}

            <div className="glass p-8 rounded-sm">
              <div className="tracking-celestial text-[var(--rb-text-muted)] mb-3">What you&apos;ll receive</div>
              <ul className="space-y-3 text-[14px] text-[var(--rb-text-body)] leading-relaxed">
                <li className="flex gap-3"><span className="text-[var(--rb-gold)]">✦</span> A 3D miniature world built by hand, sealed under glimmering glass</li>
                <li className="flex gap-3"><span className="text-[var(--rb-gold)]">✦</span> Their verse, set in elegant calligraphy inside the piece</li>
                <li className="flex gap-3"><span className="text-[var(--rb-gold)]">✦</span> Custom dimensions, color palettes, and small mementos welcomed</li>
                <li className="flex gap-3"><span className="text-[var(--rb-gold)]">✦</span> What you&apos;ll find inside — listed below</li>
                <li className="flex gap-3"><span className="text-[var(--rb-gold)]">✦</span> Posted to you, wrapped with care</li>
              </ul>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}
